/**
 * Standard flute fingering chart
 * Maps note names to fingering positions
 *
 * Fingering values:
 * - 1 = closed (hole covered or key pressed)
 * - 0 = open (hole uncovered or key not pressed)
 * - 0.5 = half-closed (partially covered)
 */

export interface FingeringState {
    // Main holes (0 = open, 1 = closed, 0.5 = half-closed)
    l1?: number;
    l2?: number;
    l3?: number;
    r1?: number;
    r2?: number;
    r3?: number;

    // Thumb keys
    thumbb?: number;
    thumbbflat?: number;

    // Special keys
    gsharp?: number;
    trill1?: number;
    trill2?: number;
    csharptrill?: number;
    spatulabflat?: number;
    dsharp?: number;
    lowb?: number;
    lowc?: number;
    lowdflat?: number;
    gizmo?: number;
}

export const standardFluteFingerings: Record<string, FingeringState> = {
    // Low register
    "C4":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, lowc: 1 },
    "C#4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, lowdflat: 1 },
    "Db4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, lowdflat: 1 },
    "D4":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1 },
    "D#4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "Eb4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "E4":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, dsharp: 1 },
    "F4":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, dsharp: 1 },
    "F#4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r3: 1, dsharp: 1 },
    "Gb4": { thumbb: 1, l1: 1, l2: 1, l3: 1, r3: 1, dsharp: 1 },
    "G4":  { thumbb: 1, l1: 1, l2: 1, l3: 1, dsharp: 1 },
    "G#4": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "Ab4": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "A4":  { thumbb: 1, l1: 1, l2: 1, dsharp: 1 },
    "A#4": { thumbb: 1, l1: 1, r1: 1, dsharp: 1 },
    "Bb4": { thumbb: 1, l1: 1, r1: 1, dsharp: 1 },
    "B4":  { thumbb: 1, l1: 1, dsharp: 1 },

    // Middle register
    "C5":  { l1: 1, dsharp: 1 },
    "C#5": { dsharp: 1 },
    "Db5": { l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, csharptrill: 1 },
    "D5":  { thumbb: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1 },
    "D#5": { thumbb: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "Eb5": { thumbb: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "E5":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, dsharp: 1 },
    "F5":  { thumbb: 1, l1: 1, l2: 1, l3: 1, r1: 1, dsharp: 1 },
    "F#5": { thumbb: 1, l1: 1, l2: 1, l3: 1, r3: 1, dsharp: 1 },
    "Gb5": { thumbb: 1, l1: 1, l2: 1, l3: 1, r3: 1, dsharp: 1 },
    "G5":  { thumbb: 1, l1: 1, l2: 1, l3: 1, dsharp: 1 },
    "G#5": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "Ab5": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "A5":  { thumbb: 1, l1: 1, l2:1, dsharp: 1 },
    "A#5": { thumbb: 1, l1: 1, r1: 1, dsharp: 1 },
    "Bb5": { thumbb: 1, l1: 1, r1: 1, dsharp: 1 },
    "B5":  { thumbb: 1, l1: 1, dsharp: 1 },

    // High register (third octave)
    "C6": { l1: 1, dsharp: 1 },
    "C#6": { dsharp: 1 },
    "Db6": { dsharp: 1 }, 
    "D6":  { thumbb: 1, l2: 1,  l3: 1, dsharp: 1 },
    "D#6": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "Eb6": { thumbb: 1, l1: 1, l2: 1, l3: 1, gsharp: 1, r1: 1, r2: 1, r3: 1, dsharp: 1 },
    "E6":  { thumbb: 1, l1: 1, l2: 1, r1: 1, r2: 1, dsharp: 1 },
    "F6":  { thumbb: 1, l1: 1, l3: 1, r1: 1, dsharp: 1 },
    "F#6": { thumbb: 1, l1: 1, l3: 1, r3: 1, dsharp: 1 },
    "Gb6": { thumbb: 1, l1: 1, l3: 1, r3: 1, dsharp: 1 },
    "G6":  { l1: 1, l2: 1, l3: 1, dsharp: 1 },
    "G#6": { l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "Ab6": { l2: 1, l3: 1, gsharp: 1, dsharp: 1 },
    "A6":  { thumbb: 1, l2: 1, r1: 1, dsharp: 1 },
    "A#6": { thumbb: 1, r1: 1, trill1: 1, dsharp: 1 },
    "Bb6": { thumbb: 1, r1: 1, trill1: 1, dsharp: 1 },
    "B6":  { thumbb: 1, l1: 1, l3: 1, trill2: 1 },

    // Very high notes
    "C7": { l1: 1, l2: 1, l3: 1, gsharp: 1, r1: 1, dsharp: 1 },
};

export const alternateFlueFingerings: Record<string, FingeringState> = {
  "A#4": { thumbbflat: 1, l1: 1, dsharp: 1 },
  "Bb4": { thumbbflat: 1, l1: 1, dsharp: 1 },
  "A#5": { thumbbflat: 1, l1: 1, dsharp: 1 },
  "Bb5": { thumbbflat: 1, l1: 1, dsharp: 1 },
}

/**
 * Get the standard fingering for a given note
 * @param note - Note name in format like "C4", "F#5", "Bb4"
 * @returns The fingering state for that note, or undefined if not found
 */
export function getStandardFingering(note: string): FingeringState | undefined {
    return standardFluteFingerings[note];
}

/**
 * Check if a standard fingering exists for a note
 * @param note - Note name in format like "C4", "F#5", "Bb4"
 * @returns true if a standard fingering exists
 */
export function hasStandardFingering(note: string): boolean {
    return note in standardFluteFingerings;
}

/**
 * Convert a note name to MIDI number
 * @param note - Note name in format like "C4", "F#5", "Bb4"
 * @returns MIDI note number (0-127) or undefined if invalid
 */
export function noteToMidi(note: string): number | undefined {
    const match = note.match(/^([A-G])([#b]?)(\d+)$/);
    if (!match) return undefined;

    const [, letter, accidental, octaveStr] = match;
    const octave = parseInt(octaveStr, 10);

    // C4 = MIDI 60 (middle C)
    const noteValues: Record<string, number> = {
        C: 0,
        D: 2,
        E: 4,
        F: 5,
        G: 7,
        A: 9,
        B: 11,
    };

    let midi = noteValues[letter] + (octave + 1) * 12;

    if (accidental === "#") {
        midi += 1;
    } else if (accidental === "b") {
        midi -= 1;
    }

    return midi;
}

/**
 * Convert MIDI number to note name
 * @param midi - MIDI note number (0-127)
 * @param preferFlat - Prefer flat notation for enharmonic notes (default: sharp)
 * @returns Note name in format like "C4", "F#5", "Bb4"
 */
export function midiToNote(midi: number, preferFlat: boolean = false): string {
    const octave = Math.floor(midi / 12) - 1;
    const pitchClass = midi % 12;

    const sharpNames = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
    const flatNames = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

    const noteName = preferFlat ? flatNames[pitchClass] : sharpNames[pitchClass];
    return `${noteName}${octave}`;
}

/**
 * Create a reverse mapping from MIDI numbers to note names with fingerings
 */
const midiToFingeringMap = new Map<number, Array<{ note: string; fingering: FingeringState }>>();

// Build the MIDI mapping
Object.entries(standardFluteFingerings).forEach(([note, fingering]) => {
    const midi = noteToMidi(note);
    if (midi !== undefined) {
        if (!midiToFingeringMap.has(midi)) {
            midiToFingeringMap.set(midi, []);
        }
        midiToFingeringMap.get(midi)!.push({ note, fingering });
    }
});

/**
 * Get fingering for a MIDI note number
 * @param midi - MIDI note number (0-127)
 * @param preferFlat - Prefer flat spelling for enharmonic notes
 * @returns The fingering state for that note, or undefined if not found
 */
export function getFingeringByMidi(
    midi: number,
    preferFlat: boolean = false
): FingeringState | undefined {
    const options = midiToFingeringMap.get(midi);
    if (!options || options.length === 0) return undefined;

    // If only one option, return it
    if (options.length === 1) return options[0].fingering;

    // If multiple options (enharmonic), choose based on preference
    const preferred = options.find((opt) =>
        preferFlat ? opt.note.includes("b") : opt.note.includes("#")
    );

    return preferred ? preferred.fingering : options[0].fingering;
}

/**
 * Get all available MIDI notes with fingerings
 * @returns Array of MIDI note numbers that have fingerings
 */
export function getAvailableMidiNotes(): number[] {
    return Array.from(midiToFingeringMap.keys()).sort((a, b) => a - b);
}

/**
 * Get the minimum MIDI note available on the flute
 * @returns Lowest MIDI note number with a fingering
 */
export function getMinMidiNote(): number {
    const available = getAvailableMidiNotes();
    return available[0] ?? 0;
}

/**
 * Get the maximum MIDI note available on the flute
 * @returns Highest MIDI note number with a fingering
 */
export function getMaxMidiNote(): number {
    const available = getAvailableMidiNotes();
    return available[available.length - 1] ?? 127;
}

/**
 * Flute register categories
 */
export type FluteRegister = "low" | "middle" | "high" | "very-high" | "out-of-range";

/**
 * Categorize a MIDI note into flute register ranges
 * @param midi - MIDI note number
 * @returns Register category
 */
export function getFluteRegister(midi: number): FluteRegister {
    // C4 (MIDI 60) to B4 (MIDI 71) - Low register
    if (midi >= 60 && midi <= 71) return "low";

    // C5 (MIDI 72) to B5 (MIDI 83) - Middle register
    if (midi >= 72 && midi <= 83) return "middle";

    // C6 (MIDI 84) to B6 (MIDI 95) - High register
    if (midi >= 84 && midi <= 95) return "high";

    // C7 (MIDI 96) and above - Very high register
    if (midi >= 96 && midi <= getMaxMidiNote()) return "very-high";

    return "out-of-range";
}

/**
 * Check if a MIDI note is within the flute's range
 * @param midi - MIDI note number
 * @returns true if the note has a fingering available
 */
export function isInFluteRange(midi: number): boolean {
    return midiToFingeringMap.has(midi);
}
